import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Shield, Home, ChevronDown, ChevronUp, Mail, Phone, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PoliticaPrivacidad = () => {
    const navigate = useNavigate();
    const [expandedSections, setExpandedSections] = useState({});

    const toggleSection = (sectionId) => {
        setExpandedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    const sections = [
        {
            id: 1,
            title: '1. INFORMACIÓN QUE RECOPILAMOS',
            content: (
                <>
                    <p className="mb-4">Recopilamos diferentes tipos de información para proporcionar y mejorar nuestro servicio:</p>
                    <h4 className="font-semibold text-gray-800 mb-2">1.1 Información Personal</h4>
                    <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                        <li>Nombre completo y datos de contacto (email, teléfono)</li>
                        <li>Información de perfil profesional (agentes inmobiliarios)</li>
                        <li>Documentos de identidad (cuando sea requerido por ley)</li>
                        <li>Información de pago y facturación</li>
                    </ul>
                    <h4 className="font-semibold text-gray-800 mb-2">1.2 Información de Uso</h4>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                        <li>Dirección IP, tipo de navegador y dispositivo</li>
                        <li>Páginas visitadas y tiempo de navegación</li>
                        <li>Búsquedas realizadas y propiedades vistas</li>
                        <li>Cookies y tecnologías similares</li>
                    </ul>
                </>
            )
        },
        {
            id: 2,
            title: '2. CÓMO USAMOS SU INFORMACIÓN',
            content: (
                <>
                    <p className="mb-4">Utilizamos la información recopilada para:</p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                        <li>Proporcionar, mantener y mejorar nuestros servicios</li>
                        <li>Procesar transacciones y enviar notificaciones relacionadas</li>
                        <li>Personalizar su experiencia en la plataforma</li>
                        <li>Comunicarnos con usted sobre actualizaciones y ofertas</li>
                        <li>Detectar, prevenir y abordar problemas técnicos o de seguridad</li>
                        <li>Cumplir con obligaciones legales y regulatorias</li>
                        <li>Realizar análisis y estudios de mercado</li>
                    </ul>
                </>
            )
        },
        {
            id: 3,
            title: '3. COMPARTIR INFORMACIÓN CON TERCEROS',
            content: (
                <>
                    <p className="mb-4">Podemos compartir su información con:</p>
                    <h4 className="font-semibold text-gray-800 mb-2">3.1 Proveedores de Servicios</h4>
                    <p className="mb-4">Empresas que nos ayudan a operar la plataforma (hosting, procesamiento de pagos, análisis).</p>
                    <h4 className="font-semibold text-gray-800 mb-2">3.2 Agentes Inmobiliarios</h4>
                    <p className="mb-4">Cuando solicita información sobre una propiedad, compartimos sus datos de contacto con el agente responsable.</p>
                    <h4 className="font-semibold text-gray-800 mb-2">3.3 Autoridades Legales</h4>
                    <p className="mb-4">Cuando sea requerido por ley o para proteger nuestros derechos.</p>
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-4">
                        <p className="text-sm text-yellow-800">
                            <strong>Importante:</strong> Nunca vendemos su información personal a terceros con fines de marketing.
                        </p>
                    </div>
                </>
            )
        },
        {
            id: 4,
            title: '4. COOKIES Y TECNOLOGÍAS SIMILARES',
            content: (
                <>
                    <p className="mb-4">Utilizamos cookies y tecnologías similares para:</p>
                    <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                        <li><strong>Cookies esenciales:</strong> Necesarias para el funcionamiento de la plataforma</li>
                        <li><strong>Cookies de rendimiento:</strong> Nos ayudan a entender cómo usa la plataforma</li>
                        <li><strong>Cookies de funcionalidad:</strong> Recuerdan sus preferencias</li>
                        <li><strong>Cookies de publicidad:</strong> Personalizan anuncios (con su consentimiento)</li>
                    </ul>
                    <p className="text-sm text-gray-600">
                        Puede gestionar sus preferencias de cookies en la configuración de su navegador.
                    </p>
                </>
            )
        },
        {
            id: 5,
            title: '5. SUS DERECHOS (GDPR COMPLIANT)',
            content: (
                <>
                    <p className="mb-4">Usted tiene derecho a:</p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                        <li><strong>Acceso:</strong> Solicitar una copia de sus datos personales</li>
                        <li><strong>Rectificación:</strong> Corregir datos inexactos o incompletos</li>
                        <li><strong>Eliminación:</strong> Solicitar la eliminación de sus datos ("derecho al olvido")</li>
                        <li><strong>Portabilidad:</strong> Recibir sus datos en formato estructurado</li>
                        <li><strong>Oposición:</strong> Oponerse al procesamiento de sus datos</li>
                        <li><strong>Limitación:</strong> Solicitar la restricción del procesamiento</li>
                        <li><strong>Revocación:</strong> Retirar su consentimiento en cualquier momento</li>
                    </ul>
                    <p className="mt-4 text-sm bg-blue-50 border-l-4 border-blue-400 p-4">
                        Para ejercer estos derechos, contáctenos en: <strong>info@serviciosfutura.com</strong>
                    </p>
                </>
            )
        },
        {
            id: 6,
            title: '6. SEGURIDAD DE LA INFORMACIÓN',
            content: (
                <>
                    <p className="mb-4">Implementamos medidas de seguridad técnicas y organizativas para proteger su información:</p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                        <li>Cifrado SSL/TLS para transmisión de datos</li>
                        <li>Almacenamiento seguro con cifrado en reposo</li>
                        <li>Controles de acceso basados en roles</li>
                        <li>Auditorías de seguridad regulares</li>
                        <li>Capacitación del personal en protección de datos</li>
                    </ul>
                    <p className="mt-4 text-sm text-gray-600">
                        Sin embargo, ningún método de transmisión por Internet es 100% seguro. Le recomendamos usar contraseñas fuertes y no compartirlas.
                    </p>
                </>
            )
        },
        {
            id: 7,
            title: '7. RETENCIÓN DE DATOS',
            content: (
                <>
                    <p className="mb-4">Conservamos su información personal durante el tiempo necesario para:</p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                        <li>Proporcionar nuestros servicios mientras mantenga una cuenta activa</li>
                        <li>Cumplir con obligaciones legales (ej. registros fiscales por 7 años)</li>
                        <li>Resolver disputas y hacer cumplir nuestros acuerdos</li>
                    </ul>
                    <p className="mt-4">
                        Cuando solicite la eliminación de su cuenta, eliminaremos o anonimizaremos sus datos personales,
                        excepto cuando la ley requiera su conservación.
                    </p>
                </>
            )
        },
        {
            id: 8,
            title: '8. TRANSFERENCIAS INTERNACIONALES',
            content: (
                <>
                    <p className="mb-4">
                        Sus datos pueden ser transferidos y procesados en servidores ubicados fuera de El Salvador.
                        Nos aseguramos de que estas transferencias cumplan con las leyes de protección de datos aplicables
                        mediante cláusulas contractuales estándar y otras salvaguardas apropiadas.
                    </p>
                </>
            )
        },
        {
            id: 9,
            title: '9. PRIVACIDAD DE MENORES',
            content: (
                <>
                    <p className="mb-4">
                        Nuestra plataforma no está dirigida a menores de 18 años. No recopilamos intencionalmente
                        información personal de menores. Si descubrimos que hemos recopilado datos de un menor,
                        los eliminaremos de inmediato.
                    </p>
                    <p className="text-sm text-gray-600">
                        Si es padre o tutor y cree que su hijo nos ha proporcionado información personal,
                        contáctenos inmediatamente.
                    </p>
                </>
            )
        },
        {
            id: 10,
            title: '10. CAMBIOS A ESTA POLÍTICA',
            content: (
                <>
                    <p className="mb-4">
                        Podemos actualizar esta Política de Privacidad ocasionalmente. Le notificaremos de cambios
                        significativos mediante:
                    </p>
                    <ul className="list-disc list-inside space-y-2 ml-4">
                        <li>Aviso destacado en la plataforma</li>
                        <li>Correo electrónico a su dirección registrada</li>
                        <li>Actualización de la fecha "Última actualización" al inicio de esta política</li>
                    </ul>
                    <p className="mt-4 text-sm text-gray-600">
                        Le recomendamos revisar esta política periódicamente para estar informado sobre cómo protegemos su información.
                    </p>
                </>
            )
        }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-xl p-8 md:p-12">
                {/* Breadcrumb */}
                <nav className="flex items-center text-sm text-gray-500 mb-8">
                    <Link to="/" className="hover:text-blue-600 transition-colors flex items-center gap-1">
                        <Home className="w-4 h-4" />
                        Inicio
                    </Link>
                    <span className="mx-2">/</span>
                    <span className="text-gray-900 font-medium">Política de Privacidad</span>
                </nav>

                {/* Header */}
                <div className="border-b-4 border-blue-600 pb-6 mb-10">
                    <div className="flex items-center gap-3 mb-3">
                        <Shield className="w-10 h-10 text-blue-600" />
                        <h1 className="text-4xl font-bold text-gray-900">Política de Privacidad</h1>
                    </div>
                    <p className="text-gray-500 text-sm mb-4">
                        Última actualización: <span className="font-semibold">6 de enero de 2026</span>
                    </p>
                    <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mt-4">
                        <p className="text-sm text-blue-900">
                            <strong>Compromiso:</strong> En Servicios Futura, nos comprometemos a proteger su privacidad
                            y manejar sus datos personales de manera transparente y segura, cumpliendo con GDPR y leyes
                            salvadoreñas de protección de datos.
                        </p>
                    </div>
                </div>

                {/* Contenido con Acordeón */}
                <div className="space-y-4 mb-10">
                    {sections.map((section) => (
                        <div key={section.id} className="border border-gray-200 rounded-lg overflow-hidden">
                            <button
                                onClick={() => toggleSection(section.id)}
                                className="w-full flex items-center justify-between p-5 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                            >
                                <h2 className="text-lg font-bold text-gray-900">{section.title}</h2>
                                {expandedSections[section.id] ? (
                                    <ChevronUp className="w-5 h-5 text-gray-600" />
                                ) : (
                                    <ChevronDown className="w-5 h-5 text-gray-600" />
                                )}
                            </button>
                            {expandedSections[section.id] && (
                                <div className="p-6 text-gray-700 leading-relaxed border-t border-gray-200">
                                    {section.content}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Contacto */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-8 text-white mb-8">
                    <h3 className="text-2xl font-bold mb-4">Contacto de Privacidad</h3>
                    <p className="mb-6">
                        Si tiene preguntas sobre esta Política de Privacidad o desea ejercer sus derechos,
                        contáctenos:
                    </p>
                    <div className="space-y-3">
                        <div className="flex items-center gap-3">
                            <Mail className="w-5 h-5" />
                            <span><strong>Email:</strong> info@serviciosfutura.com</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <Mail className="w-5 h-5" />
                            <span><strong>Email:</strong> info@serviciosfutura.com</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <MapPin className="w-5 h-5" />
                            <span><strong>Dirección:</strong> Av. Independencia Sur, Santa Ana, El Salvador</span>
                        </div>
                    </div>
                </div>

                {/* Botón de Acción */}
                <div className="flex justify-center">
                    <Button
                        onClick={() => navigate('/')}
                        className="px-8 py-3 text-base bg-blue-600 hover:bg-blue-700 text-white"
                    >
                        Volver al Inicio
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default PoliticaPrivacidad;
